/**
 * "Texto de abertura" (campo `text`, behavior `plone.richtext`) usado como
 * tarja do cabeçalho da listagem, no lugar do fixo "Sala de imprensa".
 *
 * O campo é rich text, então pode conter desde uma linha curta (o uso que
 * a tarja espera) até um layout inteiro em HTML. Já existe conteúdo em
 * produção do segundo tipo — `/o-mpf/concursos/.../31o-concurso` guarda
 * ~4 KB de HTML com imagens e `<div>`s ali, e hoje isso é renderizado no
 * corpo da página pelo `CollectionText`. Espremer aquilo numa tarja de
 * 11px em caixa alta destruiria a página.
 *
 * Por isso a distinção abaixo: só texto curto e sem marcação de bloco vira
 * tarja; qualquer coisa mais rica continua se comportando exatamente como
 * antes (corpo via `CollectionText`, cabeçalho oculto).
 */

const MAX_EYEBROW_LENGTH = 120;

const BLOCK_MARKUP =
  /<\s*(img|div|table|ul|ol|h[1-6]|figure|iframe|blockquote)\b/i;

const stripTags = (html: string) => html.replace(/<[^>]*>/g, ' ');

const decodeEntities = (text: string) =>
  text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    // `﻿` é o zero-width que o editor slate deixa num parágrafo vazio.
    .replace(/﻿/g, '');

export const getRichTextData = (content?: {
  text?: { data?: string } | null;
}): string => content?.text?.data || '';

/** Texto puro do campo, sem tags — vazio se não houver conteúdo real. */
export const toPlainText = (html: string): string =>
  decodeEntities(stripTags(html)).replace(/\s+/g, ' ').trim();

/** Há conteúdo de fato (ignora `<p></p>` e parágrafo só com zero-width). */
export const hasOpeningText = (html: string): boolean =>
  toPlainText(html).length > 0 || BLOCK_MARKUP.test(html);

/**
 * Curto e sem marcação de bloco: serve como tarja. Caso contrário é
 * conteúdo de corpo e deve continuar indo para o `CollectionText`.
 */
export const isEyebrowText = (html: string): boolean => {
  if (!html || BLOCK_MARKUP.test(html)) return false;
  const plain = toPlainText(html);
  return plain.length > 0 && plain.length <= MAX_EYEBROW_LENGTH;
};
